import { useState, useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { UsinasContext } from "../context/UsinasContextProvider";
import {
  formIndicatorCalculationToUsina,
  usinaToFormIndicatorCalculation,
} from "../PVSystMapper";
import { Indicators, PostUsina, Usina } from "../types";
import React from "react";
import TextInput from "../../../main/components/TextInput";
import NumberInput from "../../../main/components/NumberInput";

import { ConfirmIndicatorsChangesModal } from "./ConfirmIndicatorsChangesModal";
import { checkIfIndicatorsAreDifferent } from "../divergentIndicators";
import { } from "./PVSystForm.styles";
import { PVSystUpload } from "./PVSystUpload";
import { Grid, Typography } from "@mui/material";
import { RopMonthValuesForm } from "../../../main/components/RopMonthValuesForm";
import { getUsinas } from "../../../services/api/routes/usinas";
import { ActionAlert } from "../../../main/components/alerts/ActionAlert";
import { ConfirmationModal } from "../../../main/components/ConfirmationModal";
import { usinasApi } from "../../../services/api";
import { useNavigate } from "react-router-dom";

export const monthsSchema = yup.object().shape({
  jan: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  feb: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  mar: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  apr: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  may: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  jun: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  jul: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  aug: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  sep: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  oct: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  nov: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  dec: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
});

const schema = yup.object().shape({
  nameUsina: yup.string().required("O nome da usina é obrigatório"),
  codeUsina: yup.string().required("O código da usina é obrigatório"),
  moduleArea: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  moduleEfficiency: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  variance: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  kWpico: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  invertersAmount: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  invertersFullPower: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  latitude: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  longitude: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  kWac: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  opticalLossesPercentage: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  conversionLossesPercentage: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  inverterLossesPercentage: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  transformerLossesPercentage: yup
    .number()
    .optional()
    .nullable()
    .transform((value) => (isNaN(value) ? 0 : value)),
  prPredicted: monthsSchema.optional().nullable(),
  eGridPredicted: monthsSchema.optional().nullable(),
  irradiance: monthsSchema.optional().nullable(),
  globInc: monthsSchema.optional().nullable(),
  pvSystExpectedOpticalLoss: monthsSchema.optional().nullable(),
  pvSystExpectedConversionLoss: monthsSchema.optional().nullable(),
  pvSystExpectedInverterLoss: monthsSchema.optional().nullable(),
  pvSystExpectedTransformerLoss: monthsSchema.optional().nullable(),
  pvSystExpectedSystem: monthsSchema.optional().nullable(),
  pvSystExpectedContractual: monthsSchema.optional().nullable(),
  pvSystExpectedInverter: monthsSchema.optional().nullable(),
});

export type IndicatorsData = yup.InferType<typeof schema>;
export type MonthsSchemaData = yup.InferType<typeof monthsSchema>;

const PVSystForm = React.forwardRef(
  (
    props: {
      selectedStep: number;
      setSelectedStep: (step: number) => void;
    },
    ref
  ) => {
    const { newUsina, setNewUsina, currentIndicators, setCurrentIndicators } =
      useContext(UsinasContext);
    const {
      handleSubmit,
      control,
      formState: { errors },
      setValue,
      watch,
    } = useForm({
      resolver: yupResolver(schema),
    });
    const [
      openConfirmIndicatorsChangeModal,
      setOpenConfirmIndicatorsChangeModal,
    ] = useState(false);

    const [alertMessage, setAlertMessage] = useState("");
    const [showAddItemConfirmationModal, setShowAddItemConfirmationModal] =
      useState(false);
    const [openErrorModal, setOpenErrorModal] = useState(false);
    const [openSuccesModal, setOpenSuccessModal] = useState(false);

    const data = watch();

    const newIndicators: Indicators = {
      efficiency: data.moduleEfficiency,
      totalPower: data.kWac,
      modulesArea: data.moduleArea,
      kwPeak: data.kWpico,
      variance: data.variance,
      irradiance: data.irradiance,
      globInc: data.globInc,
      predictedEnergy: data.eGridPredicted,
      pr: data.prPredicted,
      opticalLossesPercentage: data.opticalLossesPercentage,
      conversionLossesPercentage: data.conversionLossesPercentage,
      inverterLossesPercentage: data.inverterLossesPercentage,
      transformerLossesPercentage: data.transformerLossesPercentage,
      pvSystExpectedOpticalLoss: data.pvSystExpectedOpticalLoss,
      pvSystExpectedConversionLoss: data.pvSystExpectedConversionLoss,
      pvSystExpectedInverterLoss: data.pvSystExpectedInverterLoss,
      pvSystExpectedTransformerLoss: data.pvSystExpectedTransformerLoss,
      pvSystExpectedSystem: data.pvSystExpectedSystem,
      pvSystExpectedContractual: data.pvSystExpectedContractual,
      pvSystExpectedInverter: data.pvSystExpectedInverter,
    };

    const path = window.location.pathname;
    const isRegister = path.includes("cadastrar");
    useEffect(() => {
      if (isRegister) {
        setCurrentIndicators(undefined);
      } else {
        setCurrentIndicators(newUsina?.indicators);
      }
    }, [newUsina?.indicators]);

    function updateData() {
      if (newUsina) {
        const formIndicatorData = usinaToFormIndicatorCalculation(newUsina);
        if (!formIndicatorData) return;
        setValue("nameUsina", formIndicatorData.nameUsina);
        setValue("codeUsina", formIndicatorData.codeUsina);
        setValue("kWac", formIndicatorData.kWac);
        setValue("invertersAmount", formIndicatorData.invertersAmount);
        setValue("invertersFullPower", formIndicatorData.invertersFullPower);
        setValue("latitude", formIndicatorData.latitude);
        setValue("longitude", formIndicatorData.longitude);
        setValue("variance", formIndicatorData.variance);
        setValue("kWpico", formIndicatorData.kWpico);
        setValue("moduleArea", formIndicatorData.moduleArea);
        setValue("moduleEfficiency", formIndicatorData.moduleEfficiency);
        setValue("opticalLossesPercentage", formIndicatorData.opticalLossesPercentage);
        setValue("conversionLossesPercentage", formIndicatorData.conversionLossesPercentage);
        setValue("inverterLossesPercentage", formIndicatorData.inverterLossesPercentage);
        setValue("transformerLossesPercentage", formIndicatorData.transformerLossesPercentage);
        setValue("prPredicted", formIndicatorData.prPredicted);
        setValue("irradiance", formIndicatorData.irradiance);
        setValue("globInc", formIndicatorData.globInc);
        setValue("eGridPredicted", formIndicatorData.eGridPredicted);
        setValue("pvSystExpectedOpticalLoss", formIndicatorData.pvSystExpectedOpticalLoss);
        setValue("pvSystExpectedConversionLoss", formIndicatorData.pvSystExpectedConversionLoss);
        setValue("pvSystExpectedInverterLoss", formIndicatorData.pvSystExpectedInverterLoss);
        setValue("pvSystExpectedTransformerLoss", formIndicatorData.pvSystExpectedTransformerLoss);
        setValue("pvSystExpectedSystem", formIndicatorData.pvSystExpectedSystem);
        setValue("pvSystExpectedContractual", formIndicatorData.pvSystExpectedContractual);
        setValue("pvSystExpectedInverter", formIndicatorData.pvSystExpectedInverter);
      } else {
        setValue("nameUsina", "");
        setValue("codeUsina", "");
        setValue("kWac", undefined);
        setValue("invertersAmount", undefined);
        setValue("invertersFullPower", undefined);
        setValue("latitude", undefined);
        setValue("longitude", undefined);
        setValue("variance", undefined);
        setValue("kWpico", undefined);
        setValue("moduleArea", undefined);
        setValue("opticalLossesPercentage", undefined);
        setValue("conversionLossesPercentage", undefined);
        setValue("inverterLossesPercentage", undefined);
        setValue("transformerLossesPercentage", undefined);
        clearMonthValuesData("prPredicted");
        clearMonthValuesData("irradiance");
        clearMonthValuesData("globInc");
        clearMonthValuesData("eGridPredicted");
        clearMonthValuesData("pvSystExpectedOpticalLoss");
        clearMonthValuesData("pvSystExpectedConversionLoss");
        clearMonthValuesData("pvSystExpectedInverterLoss");
        clearMonthValuesData("pvSystExpectedTransformerLoss");
        clearMonthValuesData("pvSystExpectedSystem");
        clearMonthValuesData("pvSystExpectedContractual");
        clearMonthValuesData("pvSystExpectedInverter");
        setValue("moduleEfficiency", undefined);
      }
    }
    const clearMonthValuesData = (
      key: "eGridPredicted" | "prPredicted" | "irradiance" | "globInc" | "pvSystExpectedOpticalLoss" | "pvSystExpectedConversionLoss" | "pvSystExpectedInverterLoss" | "pvSystExpectedTransformerLoss" | "pvSystExpectedSystem" | "pvSystExpectedContractual" | "pvSystExpectedInverter"
    ) => {
      const months: [
        "jan",
        "feb",
        "mar",
        "apr",
        "may",
        "jun",
        "jul",
        "aug",
        "sep",
        "oct",
        "nov",
        "dec"
      ] = [
          "jan",
          "feb",
          "mar",
          "apr",
          "may",
          "jun",
          "jul",
          "aug",
          "sep",
          "oct",
          "nov",
          "dec",
        ];
      months.forEach((month) => {
        setValue(`${key}.${month}`, undefined);
      });
    };

    useEffect(() => {
      updateData();
    }, [newUsina]);

    const [usinaByCode, setUsinaByCode] = useState<Usina | undefined>()

    const navigate = useNavigate();

    const handleConfirmReactiveUsina = async () => {
      try {
        setShowAddItemConfirmationModal(false);
        await usinasApi.patchUsinaIsRemoved(usinaByCode?.usinaIds.id ?? "");
        setOpenSuccessModal(true);
      } catch (error) {
        console.error("Erro ao reativar usina:", error);
        setAlertMessage("Erro ao reativar usina, Tente Novamente!");
        setOpenErrorModal(true);
      }
    };

    const [nextStep, setNextStep] = useState<boolean | undefined>()

    const onSubmit = async (data: IndicatorsData) => {
      const code = watch("codeUsina");
      try {
        const usinas = await getUsinas(true);

        const usina = usinas.data.find((usina) => usina.code === code);

        if (usina) {
          setUsinaByCode(usina)
          setNextStep(false)
          if (usina.isRemoved == true) {
            setShowAddItemConfirmationModal(true);
          } else {
            setAlertMessage("Já esiste usina ativa com o código digitado!");
            setOpenErrorModal(true);
          }
        } else {
          setNextStep(true)
        }
      }
      catch (error) {
        console.error("Erro ao buscar usina:", error);
      };

      const indicatorsAreDifferent = checkIfIndicatorsAreDifferent(
        "PVSystForm",
        currentIndicators,
        newIndicators
      );
      if (indicatorsAreDifferent) {
        setOpenConfirmIndicatorsChangeModal(true);
      } else {
        confirmSubmit(data, nextStep);
      }
    };

    function confirmSubmit(data: IndicatorsData, nextStep?: boolean) {
      const updatedUsina = formIndicatorCalculationToUsina(data);
      const newUsinaIndicators: PostUsina = {
        ...newUsina,
        name: updatedUsina?.name,
        code: updatedUsina?.code,
        indicators: {
          megaWattValue: newUsina?.indicators?.megaWattValue,
          ...updatedUsina?.indicators,
        },
        components: {
          ...newUsina?.components,
          inverters: {
            amount: updatedUsina?.components?.inverters?.amount,
            inverterPower: updatedUsina?.components?.inverters?.inverterPower,
            ...newUsina?.components?.inverters,
          },
        },
        location: {
          latitude: updatedUsina?.location?.latitude,
          longitude: updatedUsina?.location?.longitude,
        },
      };
      setNewUsina(newUsinaIndicators);
      if (nextStep == true) {
        props.setSelectedStep(props.selectedStep + 1)
      }
    }

    React.useImperativeHandle(ref, () => ({
      submitForm() {
        handleSubmit(onSubmit)();
      },
    }));
    return (
      <Grid
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          height: "100%",
          rowGap: "20px",
          paddingX: "10px",
        }}
      >
        <Grid
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <PVSystUpload />
        </Grid>
        <Grid
          width={"100%"}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          spacing={2}
          container
        >
          <Grid item xs={12} textAlign={"start"}>
            <Typography
              variant="h6"
              sx={{
                borderBottom: "1px solid #ccc",
                paddingBottom: 1,
                marginBottom: "15px",
              }}
            >
              Dados da Usina
            </Typography>
          </Grid>
          <Grid display={"flex"} item xs={12} md={3} justifyContent={"center"}>
            <TextInput
              label="Nome da Usina"
              required
              control={control}
              fieldName="nameUsina"
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3} justifyContent={"center"}>
            <TextInput
              label="Código da Usina"
              required
              control={control}
              fieldName="codeUsina"
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3} justifyContent={"center"}>
            <NumberInput
              label="Área dos módulos"
              fieldName="moduleArea"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3} justifyContent={"center"}>
            <NumberInput
              label="kWpico (Egrid)"
              fieldName="kWpico"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3} justifyContent={"center"}>
            <NumberInput
              label="Variância"
              fieldName="variance"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3} justifyContent={"center"}>
            <NumberInput
              label="Potência Total"
              fieldName="kWac"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3} justifyContent={"center"}>
            <NumberInput
              label="% Perdas Ópticas"
              fieldName="opticalLossesPercentage"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3} justifyContent={"center"}>
            <NumberInput
              label="% Perdas de Conversão"
              fieldName="conversionLossesPercentage"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3} justifyContent={"center"}>
            <NumberInput
              label="% Perdas do Inversor"
              fieldName="inverterLossesPercentage"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3} justifyContent={"center"}>
            <NumberInput
              label="% Perdas do Transformador"
              fieldName="transformerLossesPercentage"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3} justifyContent={"center"}>
            <NumberInput
              label="Eficiência dos módulos"
              control={control}
              errors={errors}
              fieldName="moduleEfficiency"
              sx={{
                width: "100%",
                maxWidth: "280px",
                marginBottom: "30px",
              }}
            />
          </Grid>
          <Grid item xs={12} textAlign={"start"}>
            <Typography
              variant="h6"
              sx={{
                borderBottom: "1px solid #ccc",
                paddingBottom: 1,
                marginBottom: "15px",
              }}
            >
              Inversores
            </Typography>
          </Grid>
          <Grid display={"flex"} item xl={2} xs={6} sm={4} md={3}>
            <NumberInput
              fieldName="invertersAmount"
              label="N° de Inversores"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xl={2} xs={6} sm={4} md={3}>
            <NumberInput
              fieldName="invertersFullPower"
              label="Pontência do Inversor"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
                marginBottom: "30px",
              }}
            />
          </Grid>
          <Grid item xs={12} textAlign={"start"}>
            <Typography
              variant="h6"
              sx={{
                borderBottom: "1px solid #ccc",
                paddingBottom: 1,
                marginBottom: "15px",
              }}
            >
              Localização
            </Typography>
          </Grid>
          <Grid display={"flex"} item xl={2} xs={6} sm={4} md={3}>
            <NumberInput
              fieldName="latitude"
              label="Latitude"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xl={2} xs={6} sm={4} md={3}>
            <NumberInput
              fieldName="longitude"
              label="Longitude"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                maxWidth: "280px",
                marginBottom: "30px",
              }}
            />
          </Grid>
          <RopMonthValuesForm
            control={control}
            errors={errors}
            fieldName="eGridPredicted"
            title="Energia prevista (Egrid)"
          />
          <div style={{ marginBottom: "90px" }}></div>
          <RopMonthValuesForm
            control={control}
            errors={errors}
            fieldName="irradiance"
            title="Irradiância prevista"
          />
          <div style={{ marginBottom: "90px" }}></div>
          <RopMonthValuesForm
            control={control}
            errors={errors}
            fieldName="globInc"
            title="GlobInc (kWh/m²)"
          />
          <div style={{ marginBottom: "90px" }}></div>
          <RopMonthValuesForm
            control={control}
            errors={errors}
            fieldName="prPredicted"
            title="PR Previsto"
          />
          <div style={{ marginBottom: "90px" }}></div>
          <RopMonthValuesForm
            control={control}
            errors={errors}
            fieldName="pvSystExpectedOpticalLoss"
            title="Perda Óptica Esperada"
          />
          <div style={{ marginBottom: "90px" }}></div>
          <RopMonthValuesForm
            control={control}
            errors={errors}
            fieldName="pvSystExpectedConversionLoss"
            title="Perda de Conversão Esperada"
          />
          <div style={{ marginBottom: "90px" }}></div>
          <RopMonthValuesForm
            control={control}
            errors={errors}
            fieldName="pvSystExpectedInverterLoss"
            title="Perda do Inversor Esperada"
          />
          <div style={{ marginBottom: "90px" }}></div>
          <RopMonthValuesForm
            control={control}
            errors={errors}
            fieldName="pvSystExpectedTransformerLoss"
            title="Perda do Transformador Esperada"
          />
          <div style={{ marginBottom: "90px" }}></div>
          <RopMonthValuesForm
            control={control}
            errors={errors}
            fieldName="pvSystExpectedSystem"
            title="Sistema Esperado"
          />
          <div style={{ marginBottom: "90px" }}></div>
          <RopMonthValuesForm
            control={control}
            errors={errors}
            fieldName="pvSystExpectedContractual"
            title="Contratual Esperado"
          />
          <div style={{ marginBottom: "90px" }}></div>
          <RopMonthValuesForm
            control={control}
            errors={errors}
            fieldName="pvSystExpectedInverter"
            title="Inversor Esperado"
          />
        </Grid>

        <ConfirmIndicatorsChangesModal
          open={openConfirmIndicatorsChangeModal}
          setOpen={setOpenConfirmIndicatorsChangeModal}
          onConfirm={confirmSubmit}
          newIndicators={newIndicators}
          fromScreen="PVSystForm"
          formData={data}
        />
        <ActionAlert
          message={alertMessage}
          severity="warning"
          open={openErrorModal}
          setOpen={() => setOpenErrorModal(true)}
          onClose={() => setOpenErrorModal(false)}
        />
        <ActionAlert
          message={"usina reativada com sucesso!"}
          severity="success"
          open={openSuccesModal}
          setOpen={() => setOpenSuccessModal(true)}
          onClose={() => { setOpenSuccessModal(false); navigate("/usinas"); }}
        />
        <ConfirmationModal
          open={showAddItemConfirmationModal}
          setOpen={setShowAddItemConfirmationModal}
          title="Reativar Usina"
          contentText={`Existe usina inativa com o código digitado, deseja confirmar a reativação da usina ${watch(
            "codeUsina"
          )}?`}
          onConfirm={handleConfirmReactiveUsina}
          setOpenErrorModal={setOpenErrorModal}
          setOpenSuccessModal={setOpenSuccessModal}
        />
      </Grid>
    );
  }
);
export default PVSystForm;
